<template>
  <!-- XOL-604 (UX spec sections 2 and 3, R2.3.6 / D7 copy): "Forgot password?". Route
       /forgot-password, rendered by Login.vue in mode `forgot`. The form asks for the
       Login ID only; the answer is the SAME confirmation whatever the Login ID was. -->
  <login-panel>

    <!-- 3. Check your e-mail: one state for every outcome. -->
    <template v-if="sent">
      <p class="login-panel__icon"><i class="fa fa-envelope-open text-primary" aria-hidden="true"></i></p>
      <h4 ref="heading" class="login-panel__title" tabindex="-1" style="text-align: center;">Check your e-mail</h4>
      <p class="login-panel__text">If &ldquo;<b>{{ sentIdentifier }}</b>&rdquo; is a Login ID, we have sent a link to its e-mail address.</p>
      <p class="login-panel__text">The link expires in 60 minutes.</p>
      <p class="login-panel__text">No e-mail after a few minutes? Check your spam or junk folder. If you have forgotten your Login ID, or your account has no e-mail address, ask your administrator.</p>
      <div class="login-panel__actions">
        <div><el-button type="primary" icon="fa fa-right-to-bracket" @click="backToLogin">Back to login</el-button></div>
        <div><el-button type="text" @click="tryAnother">Try a different Login ID</el-button></div>
      </div>
    </template>

    <!-- 2. The form. -->
    <template v-else>
      <h4 ref="heading" class="login-panel__title" tabindex="-1">Forgot your password?</h4>
      <p class="login-panel__text">Enter your Login ID. We will e-mail you a link to choose a new password.</p>

      <el-form label-position="top" @submit.native.prevent>
        <el-form-item label="Login ID" :error="fieldError">
          <el-input ref="identifier" v-model="identifier" maxlength="80" autocomplete="username"
            :disabled="submitting" @keyup.native.enter="submit" />
        </el-form-item>

        <el-alert v-if="alert === 'rate'" type="warning" show-icon :closable="false"
          title="Too many requests." description="Wait one minute, then try again." />
        <el-alert v-if="alert === 'error'" type="error" show-icon :closable="false"
          title="Couldn't send your request." description="Check your connection, then click Send reset link again." />

        <div class="login-panel__actions">
          <div>
            <el-button type="primary" icon="fa fa-paper-plane" :loading="submitting"
              :disabled="submitting || !trimmed" @click="submit">{{ submitting ? 'Sending…' : 'Send reset link' }}</el-button>
          </div>
          <div><el-button type="text" icon="fa fa-chevron-left" @click="backToLogin">Back to login</el-button></div>
        </div>
      </el-form>
    </template>

  </login-panel>
</template>

<script>

import axios from '@/lib/axiosClient'
import LoginPanel from '@/components/login/LoginPanel'
import { errStatus } from '@/lib/linkErrors'

export default {

  name: 'forgot-password',

  components: {
    LoginPanel
  },

  props: {
    // The Login field's text on /login, handed over in memory by Login.vue. Never in the URL.
    prefill: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      identifier: this.prefill || '',
      submitting: false,
      // The 422 line under the field.
      fieldError: '',
      // null | 'rate' (429) | 'error' (anything else)
      alert: null,
      sent: false,
      sentIdentifier: ''
    }
  },

  computed: {
    trimmed () {
      return (this.identifier || '').trim()
    }
  },

  methods: {

    submit () {
      if (this.submitting || !this.trimmed) {
        return
      }

      var login = this.trimmed
      this.submitting = true
      this.fieldError = ''
      this.alert = null

      // axios from the shared client (X-API-Key, X-Request-Id, retry), NOT saveForm: its
      // generic toasts would contradict the one anti-enumeration answer. The body is `{login}`
      // and nothing else (D7); the API ignores `email` / `identifier`.
      axios.post(this.getEndpoint('password/forgot', null, false), { login: login })
        .then(() => {
          // ANY 2xx is the same confirmation. The body is never read: the answer must not
          // depend on whether the Login ID matched, and the page must not either.
          this.submitting = false
          this.sentIdentifier = login
          this.sent = true
          this.focusHeading()
        }, err => {
          this.submitting = false
          var status = errStatus(err)

          if (status === 422) {
            this.fieldError = 'Enter your Login ID.'
            this.focusField()
          } else if (status === 429) {
            this.alert = 'rate'
          } else {
            this.alert = 'error'
          }
        })
    },

    backToLogin () {
      this.$router.push('/login')
    },

    // Back to the form with the field cleared and focused.
    tryAnother () {
      this.sent = false
      this.sentIdentifier = ''
      this.identifier = ''
      this.fieldError = ''
      this.alert = null
      this.focusField()
    },

    focusField () {
      this.$nextTick(() => {
        var input = this.$refs.identifier
        if (input && typeof input.focus === 'function') {
          input.focus()
        }
      })
    },

    focusHeading () {
      this.$nextTick(() => {
        var heading = this.$refs.heading
        if (heading && typeof heading.focus === 'function') {
          heading.focus()
        }
      })
    }

  },

  mounted () {
    // The field is focused when the page opens (spec section 2, "Empty").
    this.focusField()
  }

}

</script>
